import { CarouselProps, ControlProps, ScrollMode } from 'nuka-carousel';
import beTwMerge from '@/utils/beTwMerge';
import Button from '@/components/homepage/Button';
import { getAnchorId, getComponentMargin } from '@/utils/builderUtils';
import RichText from '@/components/RichText/RichText';
import useMediaQuery from '@/hooks/useMediaQuery';
import { IHomePageItem } from '@/components/Carousel/types';
import Slider from '@/components/Slider/Slider';
import LeftNavArrow from '@/components/Carousel/LeftNavArrow';
import RightNavArrow from '@/components/Carousel/RightNavArrow';
import HomepageItem from './HomepageItem';

type HomepageCarouselUpdatedProps = {
  anchorLinkID: string;
  cta?: {
    label: string;
    link: string;
  };
  isFullBleed: boolean;
  isMobileCarousel: boolean;
  isSetMaxWidth: boolean;
  items?: IHomePageItem[];
  margin: boolean;
  optionalHeader?: string;
  optionalSubHeader?: string;
  textPosition: string;
};

export default function HomepageCarouselUpdated({
  anchorLinkID,
  cta,
  isFullBleed,
  isMobileCarousel,
  isSetMaxWidth,
  items = [],
  margin,
  optionalHeader,
  optionalSubHeader,
  textPosition,
}: HomepageCarouselUpdatedProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const slidesToShow = isDesktop ? 6 : 1.5;
  const carouselConfig: CarouselProps = {
    carouselId: 'homepage-carousel-update',
    cellAlign: 'left',
    cellSpacing: 0,
    disableEdgeSwiping: true,
    dragThreshold: 0.2,
    enableKeyboardControls: true,
    scrollMode: ScrollMode.remainder,
    wrapAround: false,
    renderBottomCenterControls: () => null,
    renderCenterLeftControls: (props: ControlProps) => (
      <LeftNavArrow btnClass="tw-w-9 tw-h-9" containerClass="tw-left-2" item={items[0]} {...props} />
    ),
    renderCenterRightControls: (props: ControlProps) => (
      <RightNavArrow btnClass="tw-w-9 tw-h-9" containerClass="tw-right-2" item={items[0]} {...props} />
    ),
  };
  const isSubHeaderEmpty =
    !optionalSubHeader ||
    optionalSubHeader.length === 0 ||
    optionalSubHeader === ' ' ||
    optionalSubHeader === '&nbsp;' ||
    optionalSubHeader === '' ||
    optionalSubHeader === '<p><br /></p>' ||
    optionalSubHeader === '<p><br /></p>\n' ||
    optionalSubHeader === '<p><br /></p>' ||
    optionalSubHeader === '<p><br></p>';

  const itemStyle = {
    '3': {
      gridContainer: 'tw-grid-cols-3',
    },
    '4': {
      gridContainer: 'tw-grid-cols-2 md:tw-grid-cols-4',
    },
    '6': {
      gridContainer: 'tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6',
    },
  };

  const isTextCenter = textPosition === 'center';

  const renderItem = () => {
    const isCarousel = (!isDesktop && isMobileCarousel) || (isDesktop && items.length > 6);
    switch (true) {
      case items.length < 3 || items.length === 5:
        return null;
      case !isCarousel:
        return (
          <div
            className={beTwMerge(
              'tw-grid tw-items-start tw-gap-y-4 tw-gap-x-2 md:tw-gap-y-6',
              itemStyle[items.length]?.gridContainer || 'tw-grid-cols-2'
            )}
          >
            {items.map(item => (
              <HomepageItem isTextCenter={isTextCenter} isFullBleed={isFullBleed} item={item} key={item.name} />
            ))}
          </div>
        );
      default: {
        return (
          <Slider isMobile={!isDesktop} slidesToShow={slidesToShow} slidesToScroll={1} {...carouselConfig}>
            {items.map(item => (
              <HomepageItem isTextCenter={isTextCenter} isFullBleed={isFullBleed} item={item} key={item.name} />
            ))}
          </Slider>
        );
      }
    }
  };

  return (
    <div {...getAnchorId(anchorLinkID)} className={getComponentMargin(margin)}>
      <div
        className={beTwMerge(
          'homepage-carousel-updated tw-mx-4 lg:tw-mx-0',
          !isFullBleed && 'lg:tw-mx-8',
          isSetMaxWidth && 'lg:tw-max-w-[1440px] min-[1504px]:tw-mx-auto',
          isMobileCarousel && 'tw-mr-0'
        )}
      >
        {optionalHeader && (
          <div
            className={beTwMerge(
              'tw-flex tw-text-left tw-justify-left md:tw-justify-between tw-items-center tw-ml-0',
              !isSubHeaderEmpty ? 'tw-mb-[8px] desktop:tw-mb-[16px]' : 'tw-mb-6',
              isFullBleed ? 'desktop:tw-mx-[32px]' : 'lg:tw-ml-0',
              isTextCenter && 'tw-text-center tw-justify-center md:tw-justify-center'
            )}
          >
            <h2 className="tw-mb-0">{optionalHeader}</h2>
            {cta && cta.label && !isTextCenter && (
              <Button
                className="tw-hidden md:tw-block tw-font-avenirmedium tw-text-[14px]"
                href={cta.link}
                type="link"
                label={cta.label}
              />
            )}
          </div>
        )}

        {!isSubHeaderEmpty && (
          <RichText
            content={optionalSubHeader}
            className={`tw-mb-6 ${isTextCenter ? 'tw-text-center' : 'tw-text-left'} ${isFullBleed && 'desktop:tw-mx-[32px]'}`}
          />
        )}

        {renderItem()}

        {cta && cta.label && (
          <div className="tw-flex tw-justify-center tw-mt-8 tw-leading-[41px]">
            <Button
              className={beTwMerge(
                'tw-block tw-font-avenirmedium tw-text-[14px] tw-max-w-none',
                isMobileCarousel && 'tw-mr-4 large:tw-mr-0',
                !isTextCenter && 'md:tw-hidden'
              )}
              href={cta.link}
              type="secondary"
              label={cta.label}
            />
          </div>
        )}
      </div>
    </div>
  );
}
